import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ArrowUpRight, MapPin, Sparkles, Calendar, Filter, ChevronDown, CalendarPlus, X } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

/* ============================================================
   Calendar provider helpers
   ----------------------------------------------------------------
   Each helper returns a URL that, when opened in a new tab,
   shows the user's calendar of choice with a pre-filled event.

   • Google Calendar — deep-link with full WEEKLY recurrence rule.
   • Apple Calendar  — link to our backend .ics endpoint which
                       serves Content-Type: text/calendar so iOS
                       and macOS Safari open Calendar's "Add Event"
                       sheet directly (no file save dialog).
   • Outlook         — deep-link to Outlook.com Calendar compose.
   ============================================================ */
const DAY_TO_RRULE = { Mon: 'MO', Tue: 'TU', Wed: 'WE', Thu: 'TH', Fri: 'FR', Sat: 'SA', Sun: 'SU' };
const DAY_TO_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function parseTimeToHM(t) {
  const m = String(t).match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return { h: 0, min: 0 };
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const pm = /PM/i.test(m[3]);
  if (pm && h !== 12) h += 12;
  if (!pm && h === 12) h = 0;
  return { h, min };
}

function nextOccurrenceLocal(day, timeStr) {
  const { h, min } = parseTimeToHM(timeStr);
  const target = DAY_TO_INDEX[day];
  if (target === undefined) return new Date();
  const now = new Date();
  const delta = (target - now.getDay() + 7) % 7;
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + delta, h, min, 0, 0);
  if (delta === 0 && next.getTime() <= now.getTime()) {
    next.setDate(next.getDate() + 7);
  }
  return next;
}

function pad2(n) { return String(n).padStart(2, '0'); }
function fmtLocalCompact(d) {
  // YYYYMMDDTHHmm00 — Google Calendar's "floating" format (no Z).
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}T${pad2(d.getHours())}${pad2(d.getMinutes())}00`;
}
function fmtOutlookIso(d) {
  // YYYY-MM-DDTHH:mm:ss
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
}

function googleCalendarUrl({ day, time, title, location, durationMin }) {
  const start = nextOccurrenceLocal(day, time);
  const end = new Date(start.getTime() + (durationMin || 60) * 60 * 1000);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${title} — FitnessPro Galt`,
    dates: `${fmtLocalCompact(start)}/${fmtLocalCompact(end)}`,
    details: 'Weekly class at FitnessPro Galt. All classes included in membership. Bring your own mat for Yoga and Pilates.',
    location: `FitnessPro Galt · ${location || ''}`,
    ctz: 'America/Los_Angeles',
    recur: `RRULE:FREQ=WEEKLY;BYDAY=${DAY_TO_RRULE[day] || 'MO'}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function outlookCalendarUrl({ day, time, title, location, durationMin }) {
  // Outlook deep-link doesn't support RRULE via URL; single event only.
  const start = nextOccurrenceLocal(day, time);
  const end = new Date(start.getTime() + (durationMin || 60) * 60 * 1000);
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: `${title} — FitnessPro Galt`,
    startdt: fmtOutlookIso(start),
    enddt: fmtOutlookIso(end),
    body: 'Weekly class at FitnessPro Galt. All classes included in membership. Bring your own mat for Yoga and Pilates. (Recurring — please set the weekly repeat in Outlook after import.)',
    location: `FitnessPro Galt · ${location || ''}`,
    allday: 'false',
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function appleCalendarUrl({ day, time, title, location, durationMin }) {
  // Hits our backend route which returns Content-Type: text/calendar.
  // On iOS/macOS Safari that triggers the Calendar app "Add Event" sheet.
  const params = new URLSearchParams({
    day,
    time,
    title,
    location: location || '',
    duration: String(durationMin || 60),
  });
  return `${BACKEND_URL}/api/calendar/event.ics?${params.toString()}`;
}

/* ============================================================
   CalendarMenuPortal
   ----------------------------------------------------------------
   Tiny popover rendered via React portal so it escapes the
   schedule grid's overflow-hidden container. Anchored to the
   button's bounding rect, clamped to the viewport.
   ============================================================ */
function CalendarMenuPortal({ slot, anchorRect, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!anchorRect) return null;

  // Place the menu so its right edge aligns with the button's right edge,
  // hovering just above the button by 8px. Clamp into the viewport.
  const MENU_W = 224;
  const MENU_H_EST = 188;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchorRect.right - MENU_W;
  if (left < 8) left = 8;
  if (left + MENU_W > vw - 8) left = vw - 8 - MENU_W;
  // Prefer placement ABOVE the button; if not enough room, place BELOW.
  const placeAbove = anchorRect.top > MENU_H_EST + 12;
  const top = placeAbove
    ? Math.max(8, anchorRect.top - MENU_H_EST - 8)
    : Math.min(vh - MENU_H_EST - 8, anchorRect.bottom + 8);

  const handleProviderClick = () => {
    // Close the menu immediately when a provider is chosen so the user
    // returns to the schedule grid cleanly.
    onClose();
  };

  const linkClass = 'group flex items-center justify-between gap-2 w-full px-3 py-2.5 text-left text-xs text-zinc-200 hover:bg-red-600/15 hover:text-white transition-colors';

  return createPortal(
    <>
      {/* Backdrop — single full-viewport tap-to-close layer */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-[90]"
        data-testid="calendar-menu-backdrop"
        aria-hidden="true"
      />
      {/* Menu */}
      <div
        ref={menuRef}
        role="menu"
        aria-label="Add to calendar"
        data-testid="calendar-menu"
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'fixed', top, left, width: MENU_W }}
        className="z-[100] rounded-xl border border-zinc-800 bg-zinc-950/95 backdrop-blur-md shadow-2xl shadow-black/60 overflow-hidden"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-900 bg-black/40">
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.25em] text-red-500 font-bold">ADD TO CALENDAR</div>
            <div className="text-[11px] text-white truncate">{slot.title} · {slot.time}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="calendar-menu-close"
            className="ml-2 flex-shrink-0 p-1 rounded text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <a
          href={googleCalendarUrl(slot)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleProviderClick}
          data-testid="calendar-provider-google"
          className={linkClass}
        >
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className="inline-flex w-5 h-5 rounded-md bg-white items-center justify-center text-[10px] font-bold text-zinc-900">G</span>
            Google Calendar
          </span>
          <span className="text-[8px] tracking-[0.2em] text-zinc-500 group-hover:text-red-400">RECURRING</span>
        </a>
        <a
          href={appleCalendarUrl(slot)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleProviderClick}
          data-testid="calendar-provider-apple"
          className={linkClass}
        >
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className="inline-flex w-5 h-5 rounded-md bg-zinc-200 items-center justify-center text-[10px] font-bold text-zinc-900"></span>
            Apple Calendar
          </span>
          <span className="text-[8px] tracking-[0.2em] text-zinc-500 group-hover:text-red-400">RECURRING</span>
        </a>
        <a
          href={outlookCalendarUrl(slot)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleProviderClick}
          data-testid="calendar-provider-outlook"
          className={linkClass}
        >
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className="inline-flex w-5 h-5 rounded-md bg-[#0078D4] items-center justify-center text-[10px] font-bold text-white">O</span>
            Outlook
          </span>
          <span className="text-[8px] tracking-[0.2em] text-zinc-500 group-hover:text-red-400">SINGLE</span>
        </a>
      </div>
    </>,
    document.body,
  );
}

function TiltCard({ children }) {
  const ref = useRef(null);
  const rectRef = useRef(null);
  const rafRef = useRef(0);
  const posRef = useRef({ x: 0, y: 0 });

  const onEnter = () => {
    // Skip the tilt entirely on touch devices — no hover affordance there
    // and the cost of measuring the rect would be wasted.
    if (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) return;
    if (!ref.current) return;
    rectRef.current = ref.current.getBoundingClientRect();
  };
  const onMove = (e) => {
    if (!ref.current || !rectRef.current) return;
    posRef.current.x = e.clientX;
    posRef.current.y = e.clientY;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const rect = rectRef.current;
      const el = ref.current;
      if (!rect || !el) return;
      const x = (posRef.current.x - rect.left) / rect.width - 0.5;
      const y = (posRef.current.y - rect.top) / rect.height - 0.5;
      el.style.transform = `perspective(1200px) rotateY(${x * 5}deg) rotateX(${-y * 5}deg) translateZ(4px)`;
    });
  };
  const onLeave = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    if (ref.current) ref.current.style.transform = 'perspective(1200px) rotateY(0deg) rotateX(0deg) translateZ(0)';
    rectRef.current = null;
  };
  return (
    <div ref={ref} onMouseEnter={onEnter} onMouseMove={onMove} onMouseLeave={onLeave} className="tilt-card h-full" style={{ willChange: 'transform' }}>
      {children}
    </div>
  );
}

function ScheduleChips({ schedule }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {schedule.map((s) => (
        <span key={s.day} className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-900/80 border border-zinc-800 rounded-md text-[10px]">
          <span className="text-red-500 font-bold tracking-wider">{s.day.toUpperCase()}</span>
          <span className="text-zinc-300">{s.times.join(' · ')}</span>
        </span>
      ))}
    </div>
  );
}

function PlanRow({ plan, isLast }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`${isLast ? '' : 'border-b border-zinc-900'}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-cursor="hover"
        data-testid={`plan-toggle-${plan.name.toLowerCase().replace(/\W+/g, '-')}`}
        className="w-full flex items-center justify-between gap-3 py-3 text-left group/plan"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="font-display text-base text-white tracking-wide truncate">{plan.name}</div>
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 mt-0.5 flex items-center gap-1.5">
              <Clock className="w-2.5 h-2.5" /> {plan.duration}
              <span className="text-zinc-700">·</span>
              <span>{plan.schedule.length} {plan.schedule.length === 1 ? 'DAY' : 'DAYS'}/WK</span>
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 group-hover/plan:text-red-500 transition-transform duration-300 flex-shrink-0 ${expanded ? 'rotate-180 text-red-500' : ''}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${expanded ? 'grid-rows-[1fr] pb-4' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className="text-xs text-zinc-400 leading-relaxed pl-1 mb-3">{plan.description}</p>
          <ScheduleChips schedule={plan.schedule} />
        </div>
      </div>
    </div>
  );
}

function ProgramCard({ program, idx }) {
  return (
    <TiltCard>
      <div className="group relative bg-black border border-zinc-900 hover:border-red-600/50 rounded-2xl overflow-hidden transition-colors duration-500 h-full flex flex-col">
        <div className="relative h-56 overflow-hidden">
          <img
            src={program.image}
            alt={program.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            <span className="px-2.5 py-1 bg-red-600 text-[9px] tracking-[0.2em] font-bold rounded-full">
              {program.plans.length} {program.plans.length === 1 ? 'PLAN' : 'PLANS'}
            </span>
            <span className="px-2.5 py-1 bg-black/80 backdrop-blur border border-white/10 text-[9px] tracking-[0.2em] font-bold rounded-full text-white inline-flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5 text-red-400" />{program.location}
            </span>
          </div>
          <div className="absolute bottom-3 left-3 text-[10px] tracking-[0.3em] text-red-500 font-bold">
            {String(idx + 1).padStart(2, '0')}
          </div>
          <div className="absolute bottom-3 right-3 text-[10px] tracking-[0.3em] text-zinc-200 bg-black/60 backdrop-blur px-2.5 py-1 rounded-full">
            {program.tagline}
          </div>
        </div>

        <div className="tilt-inner p-5 relative flex flex-col flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display text-3xl tracking-wide text-white group-hover:text-red-500 transition-colors leading-tight">
              {program.title}
            </h3>
            <ArrowUpRight className="w-5 h-5 text-zinc-500 group-hover:text-red-500 group-hover:rotate-45 transition-all duration-500 flex-shrink-0" />
          </div>
          <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">{program.blurb}</p>

          <div className="mt-5 pt-4 border-t border-zinc-900 flex-1">
            <div className="text-[9px] tracking-[0.3em] text-red-500 font-bold mb-2 flex items-center gap-2">
              PLANS OFFERED
              <span className="flex-1 h-px bg-zinc-900" />
            </div>
            <div className="space-y-0">
              {program.plans.map((p, i) => (
                <PlanRow key={p.name} plan={p} isLast={i === program.plans.length - 1} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </TiltCard>
  );
}

// Weekly schedule grid — Day columns × Time rows, collapsed into
// a dropdown by default so it never overwhelms the page.
function WeeklyScheduleGrid() {
  const { content } = useSiteContent();
  const scheduleDays = content.scheduleDays || [];
  const classPrograms = content.classPrograms || [];
  const [activeDay, setActiveDay] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [menuState, setMenuState] = useState(null); // { slot, anchorRect } | null
  const openMenuFor = (e, slot) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuState({ slot, anchorRect: rect });
  };
  const closeMenu = () => setMenuState(null);
  const grid = useMemo(() => {
    // Map each classProgram.id → its parent category so the schedule
    // grid can colour each slot consistently with the legend.
    const programCategory = {
      bootcamp: 'strength', // Boot Camp, Senior Fit
      spin: 'cycling',
      zumba: 'dance', // Zumba, Zumba Gold
      yoga: 'mindbody', // All Yoga variants (Core & Balance, Restorative, All Levels, Yoga for Strength, Yoga for Flexibility, Yogalates)
      pilates: 'mindbody', // Pilates, Pilates Fusion
    };
    const obj = {};
    scheduleDays.forEach((d) => (obj[d] = []));
    // Flatten classPrograms → plan rows (each plan has schedule)
    classPrograms.forEach((program) => {
      program.plans.forEach((plan) => {
        plan.schedule.forEach((s) => {
          if (!obj[s.day]) return;
          s.times.forEach((t) => {
            obj[s.day].push({
              day: s.day,
              time: t,
              title: plan.name,
              category: programCategory[program.id] || 'strength',
              location: program.location,
              durationMin: parseInt(((plan.duration || '60').match(/\d+/) || ['60'])[0], 10) || 60,
            });
          });
        });
      });
    });
    const toMin = (t) => {
      const m = t.match(/(\d+):(\d+)\s*(AM|PM)/);
      if (!m) return 0;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const pm = m[3] === 'PM';
      if (pm && h !== 12) h += 12;
      if (!pm && h === 12) h = 0;
      return h * 60 + min;
    };
    Object.keys(obj).forEach((d) => obj[d].sort((a, b) => toMin(a.time) - toMin(b.time)));
    return obj;
  }, [scheduleDays, classPrograms]);

  // Each category gets a coordinated set of styles:
  //   - `border` + `bg` for the resting card surface
  //   - `accent` = solid-coloured left bar (3px) for instant identification
  //   - `dot` = small filled pip in the top-right of the card
  //   - `hover` border colour for the lifted state
  // This keeps the design premium (dark + red brand) but makes the
  // category instantly readable at a glance.
  const catStyle = {
    strength: {
      border: 'border-red-600/40 bg-red-600/[0.06] text-red-200/90',
      accent: 'bg-red-500',
      dot: 'bg-red-500',
      hover: 'hover:border-red-500/80 hover:shadow-[0_8px_24px_-12px_rgba(239,43,43,0.5)]',
    },
    cycling: {
      border: 'border-orange-500/35 bg-orange-500/[0.06] text-orange-200/90',
      accent: 'bg-orange-500',
      dot: 'bg-orange-500',
      hover: 'hover:border-orange-400/80 hover:shadow-[0_8px_24px_-12px_rgba(249,115,22,0.5)]',
    },
    dance: {
      border: 'border-pink-500/35 bg-pink-500/[0.06] text-pink-200/90',
      accent: 'bg-pink-500',
      dot: 'bg-pink-500',
      hover: 'hover:border-pink-400/80 hover:shadow-[0_8px_24px_-12px_rgba(236,72,153,0.5)]',
    },
    mindbody: {
      border: 'border-emerald-500/35 bg-emerald-500/[0.06] text-emerald-200/90',
      accent: 'bg-emerald-500',
      dot: 'bg-emerald-500',
      hover: 'hover:border-emerald-400/80 hover:shadow-[0_8px_24px_-12px_rgba(16,185,129,0.5)]',
    },
  };

  // Total class slots in the week — rendered in the toggle for context
  const totalSlots = useMemo(
    () => Object.values(grid).reduce((acc, arr) => acc + arr.length, 0),
    [grid],
  );

  return (
    <div className="reveal mt-20">
      {/* === Toggle button — always visible, opens the schedule === */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="weekly-schedule-content"
        data-testid="weekly-schedule-toggle"
        data-cursor="hover"
        className={`group w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 sm:py-6 rounded-2xl border bg-gradient-to-br from-zinc-950 to-black transition-all duration-500 ${expanded ? 'border-red-500 shadow-[0_24px_60px_-20px_rgba(239,43,43,0.5)]' : 'border-zinc-900 hover:border-red-600/40'}`}
      >
        <div className="flex items-center gap-3 sm:gap-5 text-left min-w-0">
          <div className="hidden sm:flex w-12 h-12 rounded-xl bg-red-600/10 border border-red-600/30 items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-red-500" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-[2px] w-6 sm:w-10 bg-red-600" />
              <span className="text-[10px] sm:text-xs tracking-[0.4em] text-red-500 font-semibold">WEEKLY SCHEDULE</span>
            </div>
            <h3 className="font-display text-lg sm:text-2xl md:text-3xl tracking-tight leading-tight truncate">SUMMER 2026 · LIVE TIMETABLE</h3>
            <div className="text-[10px] sm:text-xs tracking-[0.2em] text-zinc-500 mt-1">{totalSlots} CLASSES · {scheduleDays.length} DAYS · TAP TO {expanded ? 'HIDE' : 'VIEW'}</div>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 transition-transform duration-500 ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* === Collapsible schedule body — animated height === */}
      <div
        id="weekly-schedule-content"
        className={`grid transition-[grid-template-rows,opacity,margin] duration-500 ease-out ${expanded ? 'grid-rows-[1fr] opacity-100 mt-5' : 'grid-rows-[0fr] opacity-0 mt-0'}`}
      >
        <div className="overflow-hidden">
          {/* Legend */}
          <div className="flex items-center justify-between gap-3 sm:gap-4 mb-4 flex-wrap">
            <div className="inline-flex items-center gap-1.5 text-[9px] sm:text-[10px] tracking-[0.2em] text-zinc-500">
              <CalendarPlus className="w-3 h-3 text-red-500" />
              <span>TAP <CalendarPlus className="inline w-2.5 h-2.5 -mt-0.5 mx-0.5" /> ON ANY CLASS · CHOOSE GOOGLE, APPLE OR OUTLOOK</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 text-[9px] sm:text-[10px] tracking-[0.2em] text-zinc-500 flex-wrap">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> STRENGTH</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> CYCLING</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-pink-500" /> DANCE</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> MIND &amp; BODY</span>
            </div>
          </div>

          {/* Horizontal scroll on narrow screens so the 6-day grid never crams */}
          <div className="rounded-3xl bg-gradient-to-br from-zinc-950 to-black border border-zinc-900 overflow-hidden shadow-2xl shadow-black/40">
            <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
              <div className="min-w-[640px]">
                <div className="grid grid-cols-6 bg-black/60 border-b border-zinc-900">
                  {scheduleDays.map((d) => (
                    <button
                      key={d}
                      onClick={() => setActiveDay(activeDay === d ? null : d)}
                      data-cursor="hover"
                      className={`relative py-4 text-center transition-colors ${activeDay === d ? 'bg-red-600/15' : 'hover:bg-zinc-900/50'}`}
                    >
                      <div className="text-[9px] tracking-[0.3em] text-zinc-500 font-bold">{ ({Mon:'MONDAY',Tue:'TUESDAY',Wed:'WEDNESDAY',Thu:'THURSDAY',Fri:'FRIDAY',Sat:'SATURDAY'})[d] }</div>
                      <div className={`font-display text-2xl mt-1 ${activeDay === d ? 'text-red-500' : 'text-white'}`}>{d}</div>
                      {activeDay === d && (
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-red-500" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-6 divide-x divide-zinc-900">
                  {scheduleDays.map((d) => {
                    const dim = activeDay && activeDay !== d;
                    return (
                      <div key={d} className={`p-3 space-y-2 min-h-[280px] transition-opacity duration-300 ${dim ? 'opacity-30' : 'opacity-100'}`}>
                        {grid[d].length === 0 && (
                          <div className="text-center py-8 text-[10px] tracking-[0.3em] text-zinc-700">REST DAY</div>
                        )}
                        {grid[d].map((slot, i) => {
                          const s = catStyle[slot.category] || catStyle.strength;
                          return (
                            <div
                              key={`${d}-${i}`}
                              data-testid={`schedule-slot-${slot.category}`}
                              className={`group/slot relative overflow-hidden pl-3 pr-2.5 py-2.5 rounded-lg border ${s.border} ${s.hover} transition-all duration-300 hover:scale-[1.03] hover:-translate-y-0.5 cursor-default`}
                              style={{ animation: `fadeIn 0.5s ${i * 0.04}s both` }}
                            >
                              {/* Solid colour accent bar on the left edge — the
                                  single strongest signal for the card's category. */}
                              <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${s.accent}`} />
                              {/* Tiny coloured dot in the top-right echoes the legend. */}
                              <span className={`absolute top-2 right-2 w-1.5 h-1.5 rounded-full ${s.dot}`} />
                              <div className="text-[10px] font-bold tracking-wider opacity-90">{slot.time}</div>
                              <div className="text-xs font-semibold text-white mt-0.5 leading-tight pr-3">{slot.title}</div>
                              <div className="mt-1 flex items-center justify-between gap-1.5">
                                <div className="text-[8px] tracking-[0.2em] text-zinc-500 truncate">{slot.location}</div>
                                <button
                                  type="button"
                                  onClick={(e) => openMenuFor(e, slot)}
                                  data-testid={`slot-add-calendar-${slot.category}`}
                                  data-cursor="hover"
                                  aria-label={`Add ${slot.title} ${slot.time} ${slot.day} to your calendar`}
                                  title="Add to calendar"
                                  className="flex-shrink-0 p-1.5 rounded-md bg-zinc-900/70 hover:bg-red-600 active:bg-red-700 text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/60"
                                >
                                  <CalendarPlus className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {activeDay && (
            <div className="mt-3 text-center">
              <button onClick={() => setActiveDay(null)} className="text-[10px] tracking-[0.3em] text-zinc-500 hover:text-red-500 transition">
                CLEAR FILTER · SHOW ALL DAYS
              </button>
            </div>
          )}

          {/* Mobile-only hint */}
          <div className="sm:hidden mt-3 text-center text-[9px] tracking-[0.3em] text-zinc-600">SWIPE HORIZONTALLY TO SEE ALL DAYS</div>
        </div>
      </div>
      {/* Calendar provider chooser — appears when a calendar icon is tapped. */}
      {menuState && (
        <CalendarMenuPortal
          slot={menuState.slot}
          anchorRect={menuState.anchorRect}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

export default function Classes() {
  const { content } = useSiteContent();
  const classPrograms = content.classPrograms || [];
  const totalPlans = classPrograms.reduce((acc, p) => acc + p.plans.length, 0);
  const [programsOpen, setProgramsOpen] = useState(false);

  return (
    <section id="classes" data-testid="classes-section" className="relative py-16 sm:py-24 lg:py-32 bg-zinc-950 overflow-hidden">
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-red-600/10 blur-3xl rounded-full" />
      <div className="absolute bottom-0 right-0 w-1/4 h-1/3 bg-red-600/8 blur-3xl rounded-full" />
      <div className="absolute inset-0 grid-bg opacity-15" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 reveal gap-6">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-[2px] w-10 bg-red-600" />
              <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">CLASSES</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
              JUMP IN. <span className="text-gradient-red">JOIN ANY CLASS.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 text-sm leading-relaxed">
            Five program families. {totalPlans} distinct plans. Every class taught by a certified instructor delivering individual attention. Drop in any time — all levels welcome.
          </p>
        </div>

        {/* Info chips */}
        <div className="flex flex-wrap items-center gap-3 mb-10 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-black border border-red-600/30 rounded-full">
            <Sparkles className="w-3.5 h-3.5 text-red-500" />
            <span className="text-[10px] tracking-[0.3em] text-zinc-300 font-bold">YOGA &amp; PILATES IN THE YOGA ROOM</span>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-black border border-zinc-800 rounded-full">
            <Filter className="w-3.5 h-3.5 text-red-500" />
            <span className="text-[10px] tracking-[0.3em] text-zinc-300 font-bold">BOOT CAMP, ZUMBA &amp; SPIN IN THE BIG ROOM</span>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-black border border-zinc-800 rounded-full">
            <Calendar className="w-3.5 h-3.5 text-red-500" />
            <span className="text-[10px] tracking-[0.3em] text-zinc-300 font-bold">BRING YOUR OWN MAT</span>
          </div>
        </div>

        {/* === Programs dropdown toggle === */}
        <button
          type="button"
          onClick={() => setProgramsOpen((v) => !v)}
          aria-expanded={programsOpen}
          aria-controls="programs-content"
          data-testid="programs-toggle"
          data-cursor="hover"
          className={`group w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 sm:py-6 rounded-2xl border bg-gradient-to-br from-zinc-950 to-black transition-all duration-500 ${programsOpen ? 'border-red-500 shadow-[0_24px_60px_-20px_rgba(239,43,43,0.5)]' : 'border-zinc-900 hover:border-red-600/40'}`}
        >
          <div className="flex items-center gap-3 sm:gap-5 text-left min-w-0">
            <div className="hidden sm:flex w-12 h-12 rounded-xl bg-red-600/10 border border-red-600/30 items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-red-500" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="h-[2px] w-6 sm:w-10 bg-red-600" />
                <span className="text-[10px] sm:text-xs tracking-[0.4em] text-red-500 font-semibold">PROGRAMS</span>
              </div>
              <h3 className="font-display text-lg sm:text-2xl md:text-3xl tracking-tight leading-tight truncate">EXPLORE OUR CLASSES</h3>
              <div className="text-[10px] sm:text-xs tracking-[0.2em] text-zinc-500 mt-1">{classPrograms.length} FAMILIES · {totalPlans} PLANS · TAP TO {programsOpen ? 'HIDE' : 'VIEW'}</div>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 transition-transform duration-500 ${programsOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* === Collapsible program grid === */}
        <div
          id="programs-content"
          className={`grid transition-[grid-template-rows,opacity,margin] duration-500 ease-out ${programsOpen ? 'grid-rows-[1fr] opacity-100 mt-6' : 'grid-rows-[0fr] opacity-0 mt-0'}`}
        >
          <div className="overflow-hidden">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {classPrograms.map((program, i) => (
                <div
                  key={program.id}
                  data-testid={`program-card-${program.id}`}
                  style={{ animation: programsOpen ? `fadeIn 0.5s ${Math.min(i, 6) * 0.07}s both` : 'none' }}
                >
                  <ProgramCard program={program} idx={i} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Weekly schedule grid */}
        <WeeklyScheduleGrid />

        {/* Note */}
        <p className="text-center mt-12 text-[10px] tracking-[0.3em] text-zinc-600 font-bold">
          SCHEDULE UPDATED JUNE 2026 &middot; ALL CLASSES ARE INCLUDED IN MEMBERSHIP &middot; CLASSES ARE ONE HOUR UNLESS NOTED OTHERWISE
        </p>
      </div>
    </section>
  );
}
